/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUserSessionIssuersRequest,
  ListUserSessionIssuersSecurity,
} from "../models/operations/listusersessionissuers.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionIssuersInfiniteQuery,
  buildUserSessionIssuersQuery,
  prefetchUserSessionIssuers,
  prefetchUserSessionIssuersInfinite,
  queryKeyUserSessionIssuers,
  queryKeyUserSessionIssuersInfinite,
  UserSessionIssuersInfiniteQueryData,
  UserSessionIssuersPageParams,
  UserSessionIssuersQueryData,
} from "./userSessionIssuers.core.js";
export {
  buildUserSessionIssuersInfiniteQuery,
  buildUserSessionIssuersQuery,
  prefetchUserSessionIssuers,
  prefetchUserSessionIssuersInfinite,
  queryKeyUserSessionIssuers,
  queryKeyUserSessionIssuersInfinite,
  type UserSessionIssuersInfiniteQueryData,
  type UserSessionIssuersPageParams,
  type UserSessionIssuersQueryData,
};

export type UserSessionIssuersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUserSessionIssuers userSessionIssuers
 *
 * @remarks
 * List user_session_issuers in the caller's project.
 */
export function useUserSessionIssuers(
  request?: ListUserSessionIssuersRequest | undefined,
  security?: ListUserSessionIssuersSecurity | undefined,
  options?: QueryHookOptions<
    UserSessionIssuersQueryData,
    UserSessionIssuersQueryError
  >,
): UseQueryResult<UserSessionIssuersQueryData, UserSessionIssuersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionIssuers userSessionIssuers
 *
 * @remarks
 * List user_session_issuers in the caller's project.
 */
export function useUserSessionIssuersSuspense(
  request?: ListUserSessionIssuersRequest | undefined,
  security?: ListUserSessionIssuersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionIssuersQueryData,
    UserSessionIssuersQueryError
  >,
): UseSuspenseQueryResult<
  UserSessionIssuersQueryData,
  UserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionIssuers userSessionIssuers
 *
 * @remarks
 * List user_session_issuers in the caller's project.
 */
export function useUserSessionIssuersInfinite(
  request?: ListUserSessionIssuersRequest | undefined,
  security?: ListUserSessionIssuersSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersPageParams
  >,
  UserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersQueryError,
    InfiniteData<
      UserSessionIssuersInfiniteQueryData,
      UserSessionIssuersPageParams
    >,
    QueryKey,
    UserSessionIssuersPageParams
  >({
    ...buildUserSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listUserSessionIssuers userSessionIssuers
 *
 * @remarks
 * List user_session_issuers in the caller's project.
 */
export function useUserSessionIssuersInfiniteSuspense(
  request?: ListUserSessionIssuersRequest | undefined,
  security?: ListUserSessionIssuersSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersPageParams
  >,
  UserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    UserSessionIssuersInfiniteQueryData,
    UserSessionIssuersQueryError,
    InfiniteData<
      UserSessionIssuersInfiniteQueryData,
      UserSessionIssuersPageParams
    >,
    QueryKey,
    UserSessionIssuersPageParams
  >({
    ...buildUserSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setUserSessionIssuersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      mcpResourceId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionIssuersQueryData,
): UserSessionIssuersQueryData | undefined {
  const key = queryKeyUserSessionIssuers(...queryKeyBase);

  return client.setQueryData<UserSessionIssuersQueryData>(key, data);
}

export function invalidateUserSessionIssuers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      mcpResourceId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionIssuers", "list", ...queryKeyBase],
  });
}

export function invalidateAllUserSessionIssuers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionIssuers", "list"],
  });
}
